import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { assignToAffiliate, listAffiliates } from "@/api/commerce";
import { apiErrorMessage } from "@/lib/api";
import type { AffiliateEntityType } from "@/types/commerce";
import { AffiliateSelect } from "@/components/affiliate-select";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * Reassign-affiliate dialog. Triggered from affiliate-owned entity detail
 * pages (campaign / ad-source). Symmetric to
 * {@link ReassignAdvertiserPopover}.
 *
 * Ad-source reassignment is special on the backend: the same call closes
 * the prior `ad_source_affiliate_history` row and opens a new one. That's
 * server-side; nothing extra is required here.
 */
export interface ReassignAffiliatePopoverProps {
    entityType: AffiliateEntityType;
    entityId: number;
    /** Current affiliate id — `null` means currently unassigned. */
    currentAffiliateId: number | null;
    /** Render-prop trigger; the parent supplies the button. */
    trigger: React.ReactNode;
    onSuccess?: () => void;
    "data-testid"?: string;
}

const ENTITY_LABEL: Record<AffiliateEntityType, string> = {
    campaign: "campaign",
    ad_source: "ad source",
};

const INVALIDATE_KEYS: Record<AffiliateEntityType, string[][]> = {
    campaign: [["campaigns"], ["campaign"]],
    ad_source: [["ad-sources"], ["ad-source"]],
};

export function ReassignAffiliatePopover({
    entityType,
    entityId,
    currentAffiliateId,
    trigger,
    onSuccess,
    "data-testid": testId,
}: ReassignAffiliatePopoverProps): React.ReactNode {
    const queryClient = useQueryClient();
    const [open, setOpen] = React.useState(false);
    const [affiliateId, setAffiliateId] = React.useState<number | null>(currentAffiliateId);
    const [reason, setReason] = React.useState("");

    React.useEffect(() => {
        if (open) {
            setAffiliateId(currentAffiliateId);
            setReason("");
        }
    }, [open, currentAffiliateId]);

    const mutation = useMutation({
        mutationFn: (vars: { affiliateId: number | null; reason: string }) =>
            assignToAffiliate({
                entityType,
                entityId,
                affiliateId: vars.affiliateId,
                reason: vars.reason,
            }),
        onSuccess: () => {
            for (const key of INVALIDATE_KEYS[entityType]) {
                void queryClient.invalidateQueries({ queryKey: key });
            }
            void queryClient.invalidateQueries({ queryKey: ["affiliates"] });
            void queryClient.invalidateQueries({ queryKey: ["affiliate", "assignment-log"] });
            toast.success("Affiliate updated");
            setOpen(false);
            onSuccess?.();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Reassign failed")),
    });

    const trimmedReason = reason.trim();
    const dirty = affiliateId !== currentAffiliateId;
    const canSave = dirty && trimmedReason.length > 0 && !mutation.isPending;

    const onSubmit = (e: React.FormEvent): void => {
        e.preventDefault();
        if (!canSave) return;
        mutation.mutate({ affiliateId, reason: trimmedReason });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !mutation.isPending && setOpen(o)}>
            <div onClick={() => setOpen(true)} className="contents">
                {trigger}
            </div>
            <DialogContent data-testid={testId ?? "reassign-affiliate-dialog"}>
                <form onSubmit={onSubmit}>
                    <DialogHeader>
                        <DialogTitle>Reassign affiliate</DialogTitle>
                        <DialogDescription>
                            Pick a new affiliate for this {ENTITY_LABEL[entityType]}, or choose
                            “Unassigned” to clear the owner.
                            {entityType === "ad_source" && (
                                <>
                                    {" "}
                                    The prior history row is closed automatically and a new one
                                    is opened from now.
                                </>
                            )}{" "}
                            The reason is recorded in the assignment log.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="reassign-affiliate-select">Affiliate</Label>
                            <AffiliateSelect
                                value={affiliateId}
                                onChange={(v) => {
                                    setAffiliateId(v === undefined ? null : v);
                                }}
                                includeAll={false}
                                includeUnassigned
                                disabled={mutation.isPending}
                                data-testid="reassign-affiliate-select"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="reassign-affiliate-reason">
                                Reason <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="reassign-affiliate-reason"
                                placeholder="e.g. PartnerCo took over this ad source on 2026-06-01"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={mutation.isPending}
                                rows={3}
                                data-testid="reassign-affiliate-reason"
                            />
                            <p className="text-xs text-muted-foreground">
                                Required. Shown in the assignment-log audit trail.
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setOpen(false)}
                            disabled={mutation.isPending}
                            data-testid="reassign-affiliate-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={!canSave}
                            data-testid="reassign-affiliate-save"
                        >
                            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                            Save
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

/**
 * Inline badge + pencil that opens {@link ReassignAffiliatePopover}.
 * Mirrors {@link AdvertiserOwnerBadge} for the affiliate-owned set
 * (campaign / ad-source).
 */
export interface AffiliateOwnerBadgeProps {
    entityType: AffiliateEntityType;
    entityId: number;
    /** Current affiliate id from the entity DTO. Undefined → loading. */
    currentAffiliateId: number | null | undefined;
    compact?: boolean;
    "data-testid"?: string;
    className?: string;
    onSuccess?: () => void;
}

export function AffiliateOwnerBadge({
    entityType,
    entityId,
    currentAffiliateId,
    compact = false,
    "data-testid": testId,
    className,
    onSuccess,
}: AffiliateOwnerBadgeProps): React.ReactNode {
    const tid = testId ?? `affiliate-badge-${entityType}-${entityId}`;
    const affiliatesQuery = useQuery({
        queryKey: ["affiliates", { includeArchived: true }],
        queryFn: () => listAffiliates({ includeArchived: true }),
        staleTime: 30_000,
    });
    const currentName = React.useMemo<string>(() => {
        if (currentAffiliateId == null) return "Unassigned";
        const a = affiliatesQuery.data?.find((x) => x.id === currentAffiliateId);
        return a ? a.name : `#${currentAffiliateId}`;
    }, [affiliatesQuery.data, currentAffiliateId]);
    const isUnassigned = currentAffiliateId == null;

    return (
        <ReassignAffiliatePopover
            entityType={entityType}
            entityId={entityId}
            currentAffiliateId={currentAffiliateId ?? null}
            onSuccess={onSuccess}
            data-testid={`${tid}-dialog`}
            trigger={
                <button
                    type="button"
                    title="Reassign affiliate"
                    aria-label={`Affiliate: ${currentName}. Click to reassign.`}
                    data-testid={tid}
                    className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs hover:bg-muted",
                        className,
                    )}
                >
                    {!compact && <span className="text-muted-foreground">Affiliate:</span>}
                    <span
                        className={cn(
                            "max-w-[160px] truncate font-medium",
                            isUnassigned && "italic text-muted-foreground",
                        )}
                    >
                        {currentName}
                    </span>
                    <Pencil className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                </button>
            }
        />
    );
}
